import { Item as RadixItem } from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import {
  type ElementType,
  forwardRef,
  type ReactEventHandler,
  useRef,
  useState,
  type ReactElement,
} from 'react';

import {
  _mergeProps,
  type AriaLabelingProps,
  type DataTestId,
  type PolymorphicComponentProps,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';

import { useIsMenuItemHovered } from './hooks/useIsMenuItemHovered.js';
import { getItemSlots } from './item-slots.js';
import { MenuItemTooltipWrapper } from './ItemTooltip.js';
import * as styles from './Menu.sty.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import { useTooltipState } from '../../overlays/hooks/useTooltipState.js';
import { textStyleCSS } from '../../styles/textStyle.sty.js';

/**
 * @public
 */
export interface MenuItemOwnProps
  extends WithChildren,
    AriaLabelingProps,
    StylingProps,
    DataTestId {
  /**
   * Whether the menu item is disabled.
   * If `true`, it prevents the user from interacting with the item and the item
   * being focused.
   */
  disabled?: boolean;
  /**
   * Optional text used for typeahead purposes. By default, the typeahead
   * behavior will use the `.textContent` of the item. Use this when the content
   * is complex, or you have non-textual content inside.
   */
  textValue?: string;
  /*
   * TODO: The onSelect from the Radix MenuItem actually emits a native Event instead of a react synthetic event.
   * When trying to fix that, our the app toolkits version of typescript ~4.9 bailed due to the type complexity. Right now there is no way
   * to correct the typing on the event and keep a compatible type version.
   * This limitiation also tracks through to the `ActionGroup` and `AppHeader`.
   * APPDEV-8522
   */
  /**
   * Event handler called when the user selects an item (via mouse or keyboard).
   * Calling `event.preventDefault()` in this handler prevents the dropdown menu
   * from closing when selecting that item.
   */
  onSelect: ReactEventHandler;
}

/**
 * @public
 */
export type MenuItemProps<E extends ElementType> = PolymorphicComponentProps<
  E,
  MenuItemOwnProps
>;

/**
 * The component that contains the dropdown menu Items.
 * @public
 */
export const Item: <E extends ElementType = 'div'>(
  props: MenuItemProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    {
      children,
      disabled = false,
      onSelect,
      as = 'div' as E,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-details': ariaDetails,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    }: MenuItemProps<E>,
    forwardedRef: typeof remainingProps.ref,
  ) => {
    const slots = getItemSlots(children);
    const {
      isOpen,
      open: openTooltip,
      close: closeTooltip,
    } = useTooltipState({
      defaultOpen: false,
    });
    const [tooltipTriggerRef, setTooltipTriggerRef] =
      useState<HTMLElement | null>(null);
    const { focusProps, focusClassName } = useFocusRing({
      disabled,
    });
    const isTooltipHovered = useRef(false);
    const { isItemHovered, hoverProps } = useIsMenuItemHovered();

    return (
      <>
        <RadixItem
          data-testid={dataTestId}
          ref={forwardedRef}
          {..._mergeProps(focusProps, hoverProps, {
            // We have no choice but to enforce casting here as the native DOM Event
            // is not fully compatible with the ReactEventHandler
            onSelect: (event: Event) => {
              if (disabled) {
                event.stopPropagation();
                event.preventDefault();
                return;
              }
              // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
              // @ts-ignore Strict mode transition - needs to be investigated
              onSelect(event);
            },
            onFocus: () => slots.tooltip && openTooltip(true),
            onBlur: () =>
              slots.tooltip &&
              // Ensures isTooltipHovered state is set correctly when the tooltip is hovered.
              // Otherwise, the tooltip would close before the onMouseLeave event of the tooltip triggers
              // and the isTooltipHovered state would never be set.
              requestAnimationFrame(() => {
                if (!isTooltipHovered.current) {
                  closeTooltip();
                }
              }),
            className: consumerClassName,
            style: consumerStyle,
          })}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-details={ariaDetails}
          asChild
        >
          <Flex
            {...remainingProps}
            className={clsx(
              textStyleCSS({ textStyle: 'base-emphasized' }),
              !isItemHovered && focusClassName,
              styles.item({ disabled }),
            )}
            disabled={disabled}
            // TODO LeMa: role actually belongs on top-level element (check for side effects regarding tests after moving the attribute!)
            role="menuitem"
            aria-disabled={disabled}
            ref={setTooltipTriggerRef}
            as={as satisfies ElementType}
          >
            {slots.icon}
            <div className={styles.itemChildren}>{slots.freeform}</div>
          </Flex>
        </RadixItem>
        {isOpen && (
          <MenuItemTooltipWrapper
            triggerElement={tooltipTriggerRef}
            onMouseEnter={() => {
              isTooltipHovered.current = true;
            }}
            onMouseLeave={() => {
              isTooltipHovered.current = false;
              closeTooltip();
            }}
          >
            {slots.tooltip}
          </MenuItemTooltipWrapper>
        )}
      </>
    );
  },
);

(Item as typeof Item & { displayName: string }).displayName = 'Menu.Item';
